import React, { useState, useEffect } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Search, Eye } from "lucide-react";

const StatusBadge = ({ respondido_por, validado_por }) => {
  if (validado_por && validado_por !== "")
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
        ✓ Validado
      </span>
    );
  if (respondido_por && respondido_por !== "")
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        ● Respondido
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      ○ Pendiente
    </span>
  );
};

export default function ResultsPage() {
  const [paciente_cod, setPaciente] = useState("");
  const [documento, setDocumento] = useState("");
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const p = sessionStorage.getItem("paciente_cod");
    const d = sessionStorage.getItem("documento");
    const r = sessionStorage.getItem("resultados");
    if (p) setPaciente(p);
    if (d) setDocumento(d);
    if (r) setResultados(JSON.parse(r));
  }, []);

  const buscar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/results/`, {
        params: { paciente_cod, documento },
      });
      setResultados(res.data);
      sessionStorage.setItem("paciente_cod", paciente_cod);
      sessionStorage.setItem("documento", documento);
      sessionStorage.setItem("resultados", JSON.stringify(res.data));
    } catch {
      alert("Error consultando resultados");
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = (cod, examen) =>
    navigate(`/resultado-detalle?paciente_cod=${cod}&examen_cod=${examen}`);

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <FlaskConical className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Resultados de laboratorio</h1>
            <p className="text-sm text-gray-500">Busca y consulta resultados de pacientes</p>
          </div>
        </div>

        {/* Buscador */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Buscar paciente</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Código paciente</label>
              <input
                placeholder="Ej. 00123"
                value={paciente_cod}
                onChange={(e) => setPaciente(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscar()}
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Documento propietario</label>
              <input
                placeholder="Número de documento"
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscar()}
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
              />
            </div>
            <Button onClick={buscar} disabled={loading} className="gap-2">
              <Search className="h-4 w-4" />
              {loading ? "Buscando..." : "Buscar"}
            </Button>
          </div>
        </div>

        {/* Tabla resultados */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-brand-50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              Resultados {resultados.length > 0 && <span className="text-brand-600">({resultados.length})</span>}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {["Código", "Fecha", "Propietario", "Paciente", "Edad", "Especie", "Raza", "Examen", "Nombre examen", "Estado", "Acción"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {resultados.length > 0 ? (
                  resultados.map((r, i) => (
                    <tr key={i} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">
                          {r.paciente_cod}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.fecha}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.propietario}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">{r.paciente}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.edad}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.especie}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.raza}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-brand-600">{r.examen_cod}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.examen_nombre}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge respondido_por={r.respondido_por} validado_por={r.validado_por} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {(r.validado_por && r.validado_por !== "") ||
                        (r.respondido_por && r.respondido_por !== "") ? (
                          <Button
                            onClick={() => verDetalle(r.paciente_cod, r.examen_cod)}
                            variant="outline"
                            size="sm"
                            className="gap-1"
                          >
                            <Eye className="h-3.5 w-3.5" /> Ver
                          </Button>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <FlaskConical className="h-10 w-10 text-brand-200" />
                        <span className="text-sm">No hay resultados para mostrar.</span>
                        <span className="text-xs">Busca por código de paciente o documento</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
